import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Box,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { sentryMaskIf } from 'sentryReplayPrivacy';

const StyledList = styled(List)(({ theme }) => ({
  margin: 0,
  padding: 0,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 2),
  // Active-selection highlight, tinted from the theme primary.
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

/**
 * SEE-1834: is this option's entity an ERP employee (`MobileCodingMenu` puts the entity in
 * `data`)? Their names are PII; other coding values stay visible. Accepts the CDK v2 typename too.
 */
const isEmployeeEntity = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;
  const typename = (data as { __typename?: unknown }).__typename;
  return typeof typename === 'string' && /^(V2)?AgaveEmployee(Entity)?$/.test(typename);
};

export interface OptionItem {
  id?: string;
  name: string;
  icon?: React.ReactNode;
  action?: string;
  disabled?: boolean;
  data?: any;
  onSelect?: () => void;
}

interface MobileOptionListProps {
  options: OptionItem[];
  onSelect?: (option: OptionItem) => void;
  hasMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  isLoadingMore?: boolean;
  isLoading?: boolean;
  // When set, the matching row is highlighted as the active selection.
  selectedId?: string;
  // SEE-1834: option names are PII — mask them in session replays. ERP employee options
  // (`option.data`) are masked without it.
  maskOptionNames?: boolean;
}

const MobileOptionList = ({
  options,
  onSelect,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  isLoading = false,
  selectedId,
  maskOptionNames = false,
}: MobileOptionListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const handleSelect = (option: OptionItem) => {
    if (option.onSelect) {
      option.onSelect();
    } else if (onSelect) {
      onSelect(option);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current?.parentElement;
    if (!container || !hasMore || !onLoadMore) return;

    const handleScroll = () => {
      if (loadingRef.current || isLoadingMore || isLoading) return;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadingRef.current = true;
        Promise.resolve(onLoadMore()).finally(() => {
          loadingRef.current = false;
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, onLoadMore, isLoadingMore, isLoading]);

  return (
    <Box ref={scrollContainerRef}>
      {!isLoading && options.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <ListItemText primary="No items found" secondary="Try adjusting your search" />
        </Box>
      ) : (
        <StyledList>
          {options.map((option, index) => (
            <StyledListItem key={option.id || index} disablePadding>
              <StyledListItemButton
                onClick={() => handleSelect(option)}
                disabled={option.disabled}
                selected={!!selectedId && option.id === selectedId}
              >
                {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                <ListItemText
                  primary={option.name}
                  {...sentryMaskIf(maskOptionNames || isEmployeeEntity(option.data))}
                />
              </StyledListItemButton>
            </StyledListItem>
          ))}
        </StyledList>
      )}
      {isLoadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default MobileOptionList;
